import { useEffect, useState } from "react";

export interface TacDebugState {
  phasePlan: Array<{ dur: number; kind: string; idx?: number }>;
  cumFrac: number[];
  totalPhaseVh: number;
  tDissolve: readonly [number, number];

  scrollSectionProg: number;
  sectionProg: number;
  triggered: boolean;
  proxyT: number;

  headingProg: number;
  pathProgs: number[];
  boxProgs: number[];
  terminalProg: number;
  typingProg: number;
  chars: number;
  totalChars: number;
  wandProg: number;
  dissolveScrollProg: number;
  zoomProg: number;
  zoomEased: number;
  scale: number;
  backdropOpacity: number;
}

declare global {
  interface Window {
    __tacDebug?: TacDebugState;
  }
}

// One row per visible/animated thing. `range` indexes into cumFrac to
// pull the phase boundaries. `key` selects which live value drives the
// fill bar (the progress 0→1 within the row's range).
type Row = {
  label: string;
  color: string;
  range: [number, number] | "dissolve";
  fill?: (s: TacDebugState) => number;
  value?: (s: TacDebugState) => string;
};

const ROWS: Row[] = [
  { label: "park heading", color: "#7DD3FC", range: [0, 1], fill: (s) => s.headingProg },
  { label: "path 0", color: "#86EFAC", range: [1, 2], fill: (s) => s.pathProgs[0] ?? 0 },
  { label: "box 0", color: "#FDE047", range: [2, 3], fill: (s) => s.boxProgs[0] ?? 0 },
  { label: "path 1", color: "#86EFAC", range: [3, 4], fill: (s) => s.pathProgs[1] ?? 0 },
  { label: "box 1", color: "#FDE047", range: [4, 5], fill: (s) => s.boxProgs[1] ?? 0 },
  { label: "path 2", color: "#86EFAC", range: [5, 6], fill: (s) => s.pathProgs[2] ?? 0 },
  { label: "box 2", color: "#FDE047", range: [6, 7], fill: (s) => s.boxProgs[2] ?? 0 },
  { label: "path 3", color: "#86EFAC", range: [7, 8], fill: (s) => s.pathProgs[3] ?? 0 },
  {
    label: "typing",
    color: "#F9A8D4",
    range: [8, 9],
    fill: (s) => s.typingProg,
    value: (s) => `${s.chars}/${s.totalChars}`,
  },
  { label: "hold magic", color: "#94A3B8", range: [9, 10] },
  {
    label: "wand tap",
    color: "#FCA5A5",
    range: [10, 11],
    fill: (s) => s.wandProg,
  },
  {
    label: "dissolve",
    color: "#C4B5FD",
    range: "dissolve",
    fill: (s) => s.dissolveScrollProg,
  },
  { label: "post-tap pause", color: "#94A3B8", range: [11, 12] },
  {
    label: "zoom",
    color: "#FDBA74",
    range: [12, 13],
    fill: (s) => s.zoomProg,
    value: (s) => `scale ${s.scale.toFixed(2)}`,
  },
  {
    label: "backdrop",
    color: "#FCD34D",
    range: [12, 13],
    fill: (s) => s.backdropOpacity,
    value: (s) => `opacity ${s.backdropOpacity.toFixed(2)}`,
  },
  { label: "dark hold", color: "#94A3B8", range: [13, 14] },
];

const LABEL_WIDTH = 130;
const VALUE_WIDTH = 110;
const ROW_HEIGHT = 16;
const ROW_GAP = 2;
const HEADER_HEIGHT = 70;
const PHASE_LABEL_HEIGHT = 22;

type Position = "top" | "bottom";

export function TimelineDebug() {
  const [state, setState] = useState<TacDebugState | null>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Position>(() => {
    if (typeof window === "undefined") return "bottom";
    const stored = window.localStorage.getItem("tacDebug:position");
    return stored === "top" ? "top" : "bottom";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("tacDebug:position", position);
    }
  }, [position]);

  useEffect(() => {
    if (!visible) return;
    let raf = 0;
    const loop = () => {
      const s = window.__tacDebug;
      if (s) setState({ ...s });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "d" || e.key === "D") setVisible((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!visible) {
    return (
      <div style={{ ...hintStyle, [position]: 12 }}>
        press <kbd style={kbdStyle}>D</kbd> for cinematic timeline
      </div>
    );
  }
  if (!state) return null;

  const positionStyle: React.CSSProperties =
    position === "top"
      ? {
          top: 0,
          bottom: "auto",
          borderTop: "none",
          borderBottom: "1px solid #334155",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
        }
      : {
          bottom: 0,
          top: "auto",
          borderTop: "1px solid #334155",
          borderBottom: "none",
          boxShadow: "0 -8px 32px rgba(0, 0, 0, 0.5)",
        };

  const { cumFrac, phasePlan, tDissolve, scrollSectionProg, sectionProg, triggered, proxyT } = state;

  const rangeStart = (r: Row["range"]) =>
    r === "dissolve" ? tDissolve[0] : cumFrac[r[0]]!;
  const rangeEnd = (r: Row["range"]) =>
    r === "dissolve" ? tDissolve[1] : cumFrac[r[1]]!;

  return (
    <div style={{ ...containerStyle, ...positionStyle }}>
      {/* Header — live values */}
      <div style={headerStyle}>
        <Stat label="scrollSectionProg" value={scrollSectionProg.toFixed(4)} />
        <Stat label="sectionProg" value={sectionProg.toFixed(4)} />
        <Stat label="proxy.t" value={proxyT.toFixed(4)} />
        <Stat label="triggered" value={String(triggered)} hl={triggered ? "#86EFAC" : "#94A3B8"} />
        <Stat label="scale" value={state.scale.toFixed(2)} />
        <Stat label="backdrop" value={state.backdropOpacity.toFixed(2)} />
        <Stat label="zoomProg" value={state.zoomProg.toFixed(3)} />
        <Stat label="zoomEased" value={state.zoomEased.toFixed(3)} />
        <Stat label="dissolve" value={state.dissolveScrollProg.toFixed(3)} />
        <Stat label="chars" value={`${state.chars}/${state.totalChars}`} />
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={() => setPosition((p) => (p === "top" ? "bottom" : "top"))}
            style={pinButtonStyle}
            title={`Pin to ${position === "top" ? "bottom" : "top"}`}
          >
            pin {position === "top" ? "↓ bottom" : "↑ top"}
          </button>
          <span style={{ color: "#64748B", fontSize: 10 }}>
            press <kbd style={kbdStyle}>D</kbd> to hide
          </span>
        </div>
      </div>

      {/* Phase labels strip */}
      <div style={{ position: "relative", height: PHASE_LABEL_HEIGHT, marginLeft: LABEL_WIDTH, marginRight: VALUE_WIDTH }}>
        {phasePlan.map((p, i) => {
          const start = cumFrac[i]!;
          const end = cumFrac[i + 1]!;
          const left = `${start * 100}%`;
          const width = `${(end - start) * 100}%`;
          return (
            <div
              key={i}
              title={`${p.kind} — dur ${p.dur}vh (${((end - start) * 100).toFixed(1)}% of section)`}
              style={{
                position: "absolute",
                left,
                width,
                top: 0,
                bottom: 0,
                borderLeft: "1px solid #334155",
                fontSize: 9,
                color: "#94A3B8",
                padding: "2px 4px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.2,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              <span style={{ opacity: 0.6 }}>{i}</span> {p.kind}
            </div>
          );
        })}
        {/* Right edge marker */}
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            borderRight: "1px solid #334155",
          }}
        />
      </div>

      {/* Rows */}
      <div style={{ position: "relative", marginRight: VALUE_WIDTH }}>
        {ROWS.map((row, i) => {
          const start = rangeStart(row.range);
          const end = rangeEnd(row.range);
          const left = `${start * 100}%`;
          const width = `${(end - start) * 100}%`;
          const fill = row.fill ? row.fill(state) : 0;
          const value = row.value ? row.value(state) : row.fill ? row.fill(state).toFixed(2) : "—";
          const active = sectionProg >= start && sectionProg <= end;
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                height: ROW_HEIGHT,
                marginBottom: ROW_GAP,
              }}
            >
              <div style={{ width: LABEL_WIDTH, fontSize: 10, color: active ? "#F1F5F9" : "#94A3B8", paddingRight: 8, textAlign: "right", flexShrink: 0 }}>
                {row.label}
              </div>
              <div style={{ flex: 1, position: "relative", height: ROW_HEIGHT, background: "#0F172A" }}>
                {/* Range bar */}
                <div
                  style={{
                    position: "absolute",
                    left,
                    width,
                    top: 0,
                    bottom: 0,
                    background: `${row.color}33`,
                    border: `1px solid ${row.color}66`,
                  }}
                />
                {/* Progress fill */}
                <div
                  style={{
                    position: "absolute",
                    left,
                    width: `calc(${width} * ${fill})`,
                    top: 0,
                    bottom: 0,
                    background: row.color,
                    opacity: 0.85,
                  }}
                />
                {/* Playhead */}
                <div
                  style={{
                    position: "absolute",
                    left: `${sectionProg * 100}%`,
                    top: -1,
                    bottom: -1,
                    width: 1,
                    background: "#EF4444",
                    pointerEvents: "none",
                  }}
                />
              </div>
              <div style={{ width: VALUE_WIDTH, fontSize: 10, color: active ? "#F1F5F9" : "#64748B", paddingLeft: 8, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                {value}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scroll vs proxy indicator at bottom */}
      <div style={{ display: "flex", alignItems: "center", marginTop: 6, marginRight: VALUE_WIDTH }}>
        <div style={{ width: LABEL_WIDTH, fontSize: 10, color: "#94A3B8", paddingRight: 8, textAlign: "right", flexShrink: 0 }}>
          scroll vs proxy
        </div>
        <div style={{ flex: 1, position: "relative", height: ROW_HEIGHT, background: "#0F172A" }}>
          <div
            title="scrollSectionProg"
            style={{
              position: "absolute",
              left: `${scrollSectionProg * 100}%`,
              top: 0,
              bottom: 0,
              width: 2,
              background: "#3B82F6",
            }}
          />
          <div
            title="sectionProg"
            style={{
              position: "absolute",
              left: `${sectionProg * 100}%`,
              top: 0,
              bottom: 0,
              width: 2,
              background: "#EF4444",
            }}
          />
        </div>
        <div style={{ width: VALUE_WIDTH, fontSize: 10, color: "#94A3B8", paddingLeft: 8, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
          <span style={{ color: "#3B82F6" }}>■</span> scroll{"  "}
          <span style={{ color: "#EF4444" }}>■</span> proxy
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, hl }: { label: string; value: string; hl?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", marginRight: 16 }}>
      <span style={{ fontSize: 9, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      <span style={{ fontSize: 11, color: hl ?? "#F1F5F9", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{value}</span>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  position: "fixed",
  left: 0,
  right: 0,
  background: "rgba(15, 23, 42, 0.95)",
  color: "#F1F5F9",
  padding: 12,
  zIndex: 9999,
  fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  pointerEvents: "auto",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  marginBottom: 10,
  paddingBottom: 8,
  borderBottom: "1px solid #1E293B",
  flexWrap: "wrap",
  gap: 4,
};

const hintStyle: React.CSSProperties = {
  position: "fixed",
  right: 12,
  background: "rgba(15, 23, 42, 0.8)",
  color: "#94A3B8",
  padding: "6px 10px",
  fontSize: 11,
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  borderRadius: 6,
  zIndex: 9999,
  pointerEvents: "auto",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
};

const pinButtonStyle: React.CSSProperties = {
  background: "#1E293B",
  color: "#F1F5F9",
  border: "1px solid #334155",
  padding: "4px 10px",
  fontSize: 10,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  borderRadius: 4,
  cursor: "pointer",
};

const kbdStyle: React.CSSProperties = {
  background: "#1E293B",
  color: "#F1F5F9",
  padding: "1px 6px",
  borderRadius: 3,
  fontSize: 10,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  border: "1px solid #334155",
};
